import { HStack, PageContainer, PageHeader, Spread, VStack } from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import { DateFilter } from '@/components/DataTable/DateFilter'
import { useDateRange } from '@/components/DatePicker/hooks'
import { ExportCsvButton } from '@/components/ExportCsv/ExportCsvButton'
import { IntroductionDialog } from '@/components/IntroductionDialog'
import {
  useDefaultOutletSubtypes,
  useDefaultTerritories,
  useQueryDefaults,
} from '@/hooks/queries/defaults/useQueryDefaults'
import {
  missingOrderSearchSchema,
  missingOrdersExportQueryOptions,
  useMissingOrders,
} from '@/hooks/queries/useOrders'
import { useTablePagination } from '@/hooks/useTablePagination'
import { queryClient } from '@/main'
import { INTRO_DIALOGS } from '@/stores/tutorials'
import { createFileRoute } from '@tanstack/react-router'
import { format, parse } from 'date-fns'
import { useTranslation } from 'react-i18next'
import MissingOrderDataTable from './-components/MissingOrderDataTable'

export const Route = createFileRoute('/_auth/_modules/_app/orders/missing/')({
  component: MissingOrdersPage,
  validateSearch: (search) => missingOrderSearchSchema.parse(search),
})

function MissingOrdersPage() {
  const { t } = useTranslation('orders')

  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const { pageSize, onPageSizeChange } = useTablePagination({
    navigate,
    search,
  })

  const defaultTerritories = useDefaultTerritories()
  const defaultOutletSubtypes = useDefaultOutletSubtypes()

  useQueryDefaults({
    search,
    navigate,
    defaults: {
      territory: defaultTerritories,
      date: format(new Date(), 'yyyy-MM-dd'),
      outletSubtype: defaultOutletSubtypes,
    },
  })

  const { isPending, error, data } = useMissingOrders({
    ...search,
    date: search.date,
    page: search.page ?? 1,
    pageSize,
  })

  const { isDateInRange } = useDateRange({
    numberOfDaysBefore: 21,
    numberOfDaysAfter: 7,
  })

  const onSelectDate = (date: Date | undefined) => {
    navigate({
      search: {
        ...search,
        date: date ? format(date, 'yyyy-MM-dd') : undefined,
        // Back to the first page, because the row the current page number refers to almost
        // certainly does not exist under the new date and the table would come back empty.
        page: 1,
      },
    })
  }

  const getCsvExport = () => queryClient.ensureQueryData(missingOrdersExportQueryOptions(search))

  return (
    <>
      <IntroductionDialog
        dialogId={INTRO_DIALOGS.MISSING_ORDERS}
        title={t('tutorials.missingOrders.dialog.title')}
        guideUrl="/orders/missing-orders"
      >
        {t('tutorials.missingOrders.dialog.description')}
      </IntroductionDialog>
      <PageContainer maxWidth="wide">
        <Spread>
          <PageHeader>{t('missingOrdersPage.heading')}</PageHeader>
          <HStack gap="sm">
            <ExportCsvButton getCsvExport={getCsvExport} rowCount={+(data?.rowCount ?? 0)} />

            <DateFilter
              disabled={(date) => !isDateInRange(date)}
              defaultDate={search.date ? parse(search.date, 'yyyy-MM-dd', new Date()) : undefined}
              onSelect={onSelectDate}
            />
          </HStack>
        </Spread>

        <Card>
          <CardContent className="py-6">
            <VStack gap="md">
              <MissingOrderDataTable
                isPending={isPending}
                error={error}
                data={data}
                pageSize={pageSize}
                onPageSizeChange={onPageSizeChange}
              />
            </VStack>
          </CardContent>
        </Card>
      </PageContainer>
    </>
  )
}
